/**
 * Lightweight, fire-and-forget conversion beacon.
 *
 * Writes one row to `public.attribution_events` for every meaningful
 * conversion-shaped action (form submit, phone tap, sms tap, key CTA click).
 * This finally activates the attribution table that the audit found at 0 rows.
 *
 * - Never throws (failures are swallowed; analytics must never break UX).
 * - Pairs with the existing `trackEvent.ts` GA4/Ads layer; both fire.
 * - Persists a stable `client_id` in localStorage so events can be stitched.
 */
import { supabase } from "@/integrations/supabase/client";
import { getUtmParams, getAttribution } from "@/lib/utmParams";

const CID_KEY = "tpp_cid";

function getClientId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let cid = localStorage.getItem(CID_KEY);
    if (!cid) {
      cid = `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
      localStorage.setItem(CID_KEY, cid);
    }
    return cid;
  } catch {
    return `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
  }
}

export interface BeaconParams {
  source_table?: string;
  source_id?: string;
  value?: number;
  service?: string;
  city?: string;
  label?: string;
  location?: string;
  method?: "form" | "phone" | "sms" | "email" | "cta" | "chat";
}

/**
 * Beacon a conversion event into attribution_events.
 * Always returns; never blocks the UI.
 */
export function beaconConversion(eventName: string, params: BeaconParams = {}) {
  if (typeof window === "undefined") return;
  try {
    const utm = getUtmParams();
    const attr = getAttribution();
    const url = new URL(window.location.href);
    const payload = {
      event_name: eventName,
      source_table: params.source_table ?? null,
      source_id: params.source_id ?? null,
      client_id: getClientId(),
      gclid: url.searchParams.get("gclid") || (attr as any)?.gclid || null,
      fbclid: url.searchParams.get("fbclid") || (attr as any)?.fbclid || null,
      utm_source: (utm as any)?.utm_source ?? null,
      utm_medium: (utm as any)?.utm_medium ?? null,
      utm_campaign: (utm as any)?.utm_campaign ?? null,
      value: typeof params.value === "number" ? params.value : null,
      metadata: {
        page: url.pathname,
        referrer: document.referrer || null,
        device: navigator.userAgent.slice(0, 200),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        service: params.service ?? null,
        city: params.city ?? null,
        label: params.label ?? null,
        location: params.location ?? null,
        method: params.method ?? null,
        landing: (attr as any)?.landing_page ?? null,
        ts: Date.now(),
      },
    };
    // Fire-and-forget; intentionally not awaited.
    void supabase
      .from("attribution_events")
      .insert(payload as any)
      .then(({ error }) => {
        if (error && import.meta.env.DEV) {
          console.warn("[beacon] insert error", error.message);
        }
      });
  } catch (err) {
    if (import.meta.env.DEV) console.warn("[beacon] threw", err);
  }
}